import React, { useMemo } from "react";

const normalizeWord = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.,!?;:()[\]{}"“”'`]+$/g, "");
};

const getWordScoreColor = (score, errorType) => {
  if (errorType === "Omission") return "bg-gray-400";
  if (typeof score !== "number") return "bg-gray-300";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getPhonemeDotColor = (score) => {
  if (typeof score !== "number") return "bg-gray-300";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getWordAssessmentClasses = (score, errorType) => {
  if (errorType === "Omission") {
    return "bg-gray-50 border-gray-300 text-gray-800";
  }

  if (typeof score !== "number") {
    return "bg-white/80 border-white/60 text-gray-800";
  }

  if (score >= 80) {
    return "bg-green-50 border-green-200 text-gray-800";
  }

  if (score >= 40) {
    return "bg-yellow-50 border-yellow-200 text-gray-800";
  }

  return "bg-red-50 border-red-200 text-gray-800";
};

const buildSegmentAssessmentMap = (segmentWords = [], assessmentWords = []) => {
  let pointer = 0;

  return segmentWords.map((item) => {
    const segmentText = normalizeWord(item?.text);

    if (!segmentText) return null;

    for (let i = pointer; i < assessmentWords.length; i++) {
      if (normalizeWord(assessmentWords[i]?.Word) === segmentText) {
        pointer = i + 1;
        return assessmentWords[i];
      }
    }

    const fallback =
      assessmentWords.find(
        (word) => normalizeWord(word?.Word) === segmentText
      ) || null;

    return fallback;
  });
};

const PresentationSentencePanel = ({
  segment,
  segments,
  handlePreviousSegment,
  handleNextSegment,
  assessment,
}) => {
  const mappedAssessmentWords = useMemo(() => {
    const assessmentWords = assessment?.pronunciation?.NBest?.[0]?.Words || [];
    return buildSegmentAssessmentMap(segment?.words || [], assessmentWords);
  }, [segment?.words, assessment]);

  return (
    <section className="rounded-3xl border border-white/40 bg-white/80 backdrop-blur-sm shadow-lg shadow-slate-500/5 overflow-hidden">
      <div className="px-3 sm:px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handlePreviousSegment}
            disabled={segment?.id === 0}
            className={[
              "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
              segment?.id === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
            ].join(" ")}
          >
            <span>←</span>
            <span className="text-sm font-medium">Previous</span>
          </button>

          <div className="flex items-center gap-3 min-w-0">
            <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
              Sentence {segments ? segment?.id + 1 : 0}/{segments?.length || 0}
            </div>
          </div>

          <button
            type="button"
            onClick={handleNextSegment}
            disabled={!segments || segment?.id >= segments.length - 1}
            className={[
              "h-10 px-4 rounded-xl transition-all duration-200 flex items-center gap-2",
              !segments || segment?.id >= segments.length - 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white/70 text-gray-700 hover:bg-white border border-white/40 hover:border-white/70 hover:shadow-md",
            ].join(" ")}
          >
            <span className="text-sm font-medium">Next</span>
            <span>→</span>
          </button>
        </div>
      </div>

      <div className="px-3 sm:px-4 pb-4">
        <div className="rounded-2xl border border-blue-200/30 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 px-3 pt-3 pb-2">
          <div className="flex flex-wrap gap-2 justify-center">
            {segment?.words?.map((item, i) => {
              const matchedAssessment = mappedAssessmentWords[i];
              const pronunciation = matchedAssessment?.PronunciationAssessment;
              const phonemes = matchedAssessment?.Phonemes || [];
              const score = pronunciation?.AccuracyScore;
              const errorType = pronunciation?.ErrorType;

              return (
                <div
                  key={`${segment?.id}-${i}`}
                  className={[
                    "flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-xl shadow-sm min-w-[68px]",
                    "text-lg sm:text-xl",
                    getWordAssessmentClasses(score, errorType),
                  ].join(" ")}
                  title={
                    matchedAssessment
                      ? `${matchedAssessment?.Word || item.text}${
                          typeof score === "number" ? ` • Score: ${score}` : ""
                        }${errorType ? ` • ${errorType}` : ""}`
                      : item.text
                  }
                >
                  <div className="flex items-center justify-center gap-1 h-2">
                    {phonemes.length > 0 ? (
                      phonemes.map((ph, idx) => (
                        <span
                          key={idx}
                          className={`w-2 h-2 rounded-full ${getPhonemeDotColor(
                            ph?.PronunciationAssessment?.AccuracyScore
                          )} ring-1 ring-white shadow-sm`}
                          title={`${ph?.Phoneme || "phoneme"}: ${
                            ph?.PronunciationAssessment?.AccuracyScore ?? "N/A"
                          }`}
                        />
                      ))
                    ) : matchedAssessment ? (
                      <span
                        className={`w-2.5 h-2.5 rounded-full ${getWordScoreColor(
                          score,
                          errorType
                        )} ring-1 ring-white shadow-sm`}
                      />
                    ) : null}
                  </div>

                  <span className="text-center leading-tight break-words text-gray-800 font-medium">
                    {item.text}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PresentationSentencePanel;